/**
 * Who may do what, written once.
 *
 * docs/06: "Permission checks live in `packages/core` so the same rule runs in
 * the UI, the main process and the server RPC. The UI hiding a button is
 * convenience; the server refusing the operation is the control."
 *
 * That sentence contains a trap, and this file is built around avoiding it. A
 * permission table in TypeScript is a second copy of an access rule, and the
 * second copy is the one nobody updates - the same objection migration 0004
 * raises against a `security definer` write path. So this table is not the
 * control and does not pretend to be. The control is row-level security in
 * migration 0001, which `apply_ops` runs under because it runs as the caller.
 * What this table is for is the question a screen has to answer before it draws
 * anything: is there any point offering this? A disabled button the server
 * would have refused is a kindness; an enabled one it refuses anyway is still
 * refused.
 *
 * The drift is handled by testing rather than by trust. `permissions.test.ts`
 * in `packages/schema` parses the policies out of the migration and compares
 * them, table by table and command by command, against what is written here;
 * `permissions.live.test.ts` asks a real Postgres, once per role, to refuse
 * everything this table says it will. Either one fails the moment the two
 * disagree, which is the only way a second copy is safe to keep.
 *
 * No I/O, no imports, and nothing that knows what a screen is - this is
 * `packages/core`.
 */
export const ROLES = [
    'sales',
    'dispatch',
    'nursery',
    'admin',
    'management',
];
/**
 * Every table a client can name.
 *
 * The server-owned four - `change_log`, `applied_ops`, `xero_sync_state`,
 * `xero_outbox` - are deliberately absent. They carry no policy at all and no
 * privilege is granted on them, so there is no permission to describe: the
 * answer is no, for everybody, before a rule is consulted.
 */
export const RESOURCES = [
    'users',
    'roles',
    'user_roles',
    'devices',
    'audit_log',
    'price_tiers',
    'customers',
    'customer_contacts',
    'addresses',
    'product_categories',
    'products',
    'product_barcodes',
    'batches',
    'price_list_items',
    'customer_price_overrides',
    'quotes',
    'quote_lines',
    'sales_orders',
    'sales_order_lines',
    'document_numbers',
    'picking_slips',
    'checking_slips',
    'label_templates',
    'label_jobs',
    'label_job_items',
    'stock_movements',
    // Migration 0016, the ordering portal (docs/15).
    'availability_lists',
    'availability_items',
    'portal_accounts',
    'web_orders',
    'web_order_lines',
];
function rule(spec = {}) {
    return {
        roles: spec.roles ?? [],
        rowScopedRoles: spec.rowScopedRoles ?? [],
        own: spec.own ?? false,
    };
}
/** No policy at all, which is deny-by-default rather than an oversight. */
const NOBODY = rule();
/** `app.is_staff()`: anyone active who holds any role. */
const STAFF = rule({ roles: ROLES });
/**
 * The table, in the order migration 0001 writes the policies.
 *
 * Read it against section 9 of that file; the tests do exactly that. Where a
 * cell says `NOBODY`, the migration carries no policy for that command - the
 * stock ledger has no update policy because a mistake is corrected by a second
 * movement, and `document_numbers` has no write policy because blocks come from
 * a server routine.
 */
export const PERMISSIONS = {
    // --- Identity ---------------------------------------------------------
    users: {
        read: rule({ roles: ['admin', 'management'], own: true }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    // Reference data every screen needs to render a role name. Nobody writes it
    // from a client: a sixth role is a migration, because every policy in the
    // schema names the set and adding one without revisiting them grants nothing.
    roles: {
        read: STAFF,
        create: NOBODY,
        update: NOBODY,
    },
    user_roles: {
        read: rule({ roles: ['admin', 'management'], own: true }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    // A user sees and registers their own machines; everything else about a
    // device is admin work.
    devices: {
        read: rule({ roles: ['admin', 'management'], own: true }),
        create: rule({ roles: ['admin'], own: true }),
        update: rule({ roles: ['admin'], own: true }),
    },
    // Readable by the people who answer for it, writable by nobody: audit rows
    // come from server routines.
    audit_log: {
        read: rule({ roles: ['admin', 'management'] }),
        create: NOBODY,
        update: NOBODY,
    },
    // --- Trade accounts ---------------------------------------------------
    // Nursery is absent from every rule in this block on purpose (docs/06): a
    // nursery machine never receives customer pricing in the first place.
    price_tiers: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    customers: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    customer_contacts: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    addresses: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    // --- Catalogue --------------------------------------------------------
    // Readable by everyone who works here: a nursery machine needs the catalogue
    // to print a production label, and it carries no prices.
    product_categories: {
        read: STAFF,
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    products: {
        read: STAFF,
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    product_barcodes: {
        read: STAFF,
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    batches: {
        read: STAFF,
        create: rule({ roles: ['nursery', 'admin'] }),
        update: rule({ roles: ['nursery', 'admin'] }),
    },
    price_list_items: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    customer_price_overrides: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    // --- Sales documents --------------------------------------------------
    quotes: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    quote_lines: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    // Dispatch updates an order it is moving through the dispatch states and no
    // other row, which is a second policy in the migration and a row-scoped role
    // here. It cannot send one back to draft, cancel it, or mark it invoiced.
    sales_orders: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'], rowScopedRoles: ['dispatch'] }),
    },
    // Picking writes `quantity_picked` and nothing else, and row-level security
    // cannot say "these columns only" - that restriction lives in the intent
    // operation `set_picked_quantity` and in `apply_ops`. What is recorded here
    // is the outer boundary the policy draws.
    sales_order_lines: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'], rowScopedRoles: ['dispatch'] }),
    },
    // A device reads its own blocks. Nobody writes one from a client: they are
    // issued by a server routine, which is the whole point of SY-04.
    document_numbers: {
        read: rule({ roles: ['admin', 'management'], own: true }),
        create: NOBODY,
        update: NOBODY,
    },
    // --- Dispatch and labels ----------------------------------------------
    picking_slips: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['dispatch', 'admin'] }),
        update: rule({ roles: ['dispatch', 'admin'] }),
    },
    checking_slips: {
        read: rule({ roles: ['sales', 'dispatch', 'admin', 'management'] }),
        create: rule({ roles: ['dispatch', 'admin'] }),
        update: rule({ roles: ['dispatch', 'admin'] }),
    },
    label_templates: {
        read: STAFF,
        create: rule({ roles: ['admin'] }),
        update: rule({ roles: ['admin'] }),
    },
    // Print history is a local fact reported upward: the device that printed a
    // job owns it. Admin and management read all of it, because "who reprinted
    // 2,000 labels" is a question that will be asked (docs/06). Creating one
    // needs a role *and* ownership, so those roles are row-scoped rather than
    // outright.
    label_jobs: {
        read: rule({ roles: ['admin', 'management'], own: true }),
        create: rule({ rowScopedRoles: ['dispatch', 'nursery', 'admin'] }),
        update: rule({ roles: ['admin'], own: true }),
    },
    // Reached only through the job that owns them, which is why no role stands
    // alone here. Reading needs admin or management, or the job to be yours -
    // but the check is made through the parent row either way, so even admin is
    // row-scoped.
    label_job_items: {
        read: rule({ rowScopedRoles: ['admin', 'management'] }),
        create: rule({ own: true }),
        update: rule({ own: true }),
    },
    // --- Stock ------------------------------------------------------------
    // Insert and select only. No update policy and no delete policy is what makes
    // the ledger a ledger: a mistake is corrected by a second movement, never by
    // editing the first.
    stock_movements: {
        read: STAFF,
        create: rule({ roles: ['dispatch', 'nursery', 'admin'] }),
        update: NOBODY,
    },
    // --- The ordering portal (migration 0016, docs/15) ---------------------
    // The list carries no price, so everyone who works here may read it, as
    // with the catalogue. Who publishes is Q19; until it is answered, sales and
    // admin.
    availability_lists: {
        read: STAFF,
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    availability_items: {
        read: STAFF,
        create: rule({ roles: ['sales', 'admin'] }),
        update: rule({ roles: ['sales', 'admin'] }),
    },
    // A login is created by `invite-customer` with the service role, never from
    // a client - so nobody creates one here, admin included. Updating it is
    // disabling it; a trigger holds the customer, contact and email still.
    portal_accounts: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: NOBODY,
        update: rule({ roles: ['sales', 'admin'] }),
    },
    // Created by the portal functions only. Updating one is deciding it, which a
    // trigger holds to `received` -> `confirmed`, `declined` or `cancelled`.
    web_orders: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: NOBODY,
        update: rule({ roles: ['sales', 'admin'] }),
    },
    // The customer's words. Nobody edits them: staff adjust the sales order a
    // web order is confirmed into, not the request.
    web_order_lines: {
        read: rule({ roles: ['sales', 'admin', 'management'] }),
        create: NOBODY,
        update: NOBODY,
    },
};
function holdsAny(held, wanted) {
    return wanted.some((role) => held.includes(role));
}
/**
 * What a user holding `held` may do, as far as this side of the wire knows.
 *
 * `own` yields `some-rows` even for a user holding no role, because that is
 * what the policies say: a user whose every grant has been revoked can still
 * read their own record and the device they are sitting at. Everything else
 * about the business has already gone.
 */
export function can(held, resource, action) {
    const permitted = PERMISSIONS[resource][action];
    if (holdsAny(held, permitted.roles))
        return 'allowed';
    if (holdsAny(held, permitted.rowScopedRoles))
        return 'some-rows';
    if (permitted.own)
        return 'some-rows';
    return 'denied';
}
/**
 * Whether to offer the action at all.
 *
 * The question a button asks. `some-rows` counts as yes: the server still has
 * the last word, and a screen that hid every row-scoped action would hide
 * picking from dispatch.
 */
export function mayAttempt(held, resource, action) {
    return can(held, resource, action) !== 'denied';
}
/** Anyone active who holds any role - `app.is_staff()` on this side. */
export function isStaff(held) {
    return holdsAny(held, ROLES);
}
/** Narrow a role code that came off the wire, or out of a local mirror. */
export function isRole(value) {
    return ROLES.includes(value);
}
/** Narrow a table name that came off the wire, or out of a local mirror. */
export function isResource(value) {
    return RESOURCES.includes(value);
}
//# sourceMappingURL=permissions.js.map